import { useEffect, useRef } from 'react'

/**
 * DotMask — a single, self-contained dot-shimmer field (monologue.to style).
 *
 * Renders a grid of dots on a <canvas>, each modulated by a smooth luminance
 * "envelope" that drifts horizontally — producing the twinkle/shimmer. Done on
 * canvas (not CSS `mask-position`) so motion is cheap and predictable, with
 * built-in reduced-motion + offscreen pausing.
 *
 * Drop it in anywhere:
 *   <DotMask color="#8e98a3" accent="#4fc3e8" background="#2b2b2b"
 *            maskSrc="/mask.png" speed={1} style={{height: 432}} />
 *
 * Props (all optional):
 *   color        dot base color                         '#8e98a3'
 *   accent       color of a fraction of dots            '#4fc3e8'
 *   background   fill behind the dots                   '#2b2b2b'
 *                ('transparent' / null -> no fill; canvas stays see-through)
 *   maskSrc      URL/data-URL of greyscale luminance    null -> procedural noise
 *                envelope; if it fails to load, falls back to procedural noise
 *   speed        scroll speed multiplier (1 ≈ original) 1
 *   gap          grid spacing in px                     6
 *   dotSize      dot size in px (square/circle)          2
 *   dotShape     'square' | 'circle' | 'rect'           'square'
 *   dotWidth     rect width in px  (defaults to dotSize) undefined
 *   dotHeight    rect height in px (defaults to dotSize) undefined
 *   density      overall brightness multiplier          1
 *   vignette     0..1 center-glow / edge falloff        1
 *   accentMix    0..1 fraction of accent-colored dots   0.1
 *   seed         RNG seed (stable dot layout)           1337
 *   maskScale    stretch factor applied to the envelope 1
 *   fps          frame cap (0 = uncapped/rAF native)    0
 *   paused       force-pause                            false
 *   respectReducedMotion                                true
 *   pauseWhenOffscreen                                  true
 *   className, style  forwarded to the wrapper <div>
 */
export default function DotMask({
  color = '#8e98a3',
  accent = '#4fc3e8',
  background = '#2b2b2b',
  maskSrc = null,
  speed = 1,
  gap = 6,
  dotSize = 2,
  dotShape = 'square',
  dotWidth = undefined,
  dotHeight = undefined,
  density = 1,
  vignette = 1,
  accentMix = 0.1,
  seed = 1337,
  maskScale = 1,
  fps = 0,
  paused = false,
  respectReducedMotion = true,
  pauseWhenOffscreen = true,
  className = undefined,
  style = undefined,
}) {
  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  // mutable state shared with the animation loop without re-subscribing
  const state = useRef({})
  state.current = {
    color, accent, background, speed, gap, dotSize, dotShape, dotWidth, dotHeight,
    density, vignette, accentMix, seed, maskScale, fps, paused, respectReducedMotion,
  }

  // ---- Load (or synthesize) the luminance envelope once per maskSrc ----
  const envRef = useRef(null) // { data:Uint8ClampedArray, w, h } luminance map
  useEffect(() => {
    let cancelled = false
    const useProcedural = () => {
      if (cancelled) return
      envRef.current = makeProceduralEnvelope(512, 256)
    }
    if (!maskSrc) { useProcedural(); return }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (cancelled) return
      try {
        const c = document.createElement('canvas')
        c.width = img.naturalWidth
        c.height = img.naturalHeight
        const cx = c.getContext('2d')
        cx.drawImage(img, 0, 0)
        const { data } = cx.getImageData(0, 0, c.width, c.height)
        envRef.current = { data, w: c.width, h: c.height }
      } catch {
        useProcedural() // cross-origin tainted -> fall back
      }
    }
    img.onerror = useProcedural
    img.src = maskSrc
    return () => { cancelled = true }
  }, [maskSrc])

  // ---- Build the dot list whenever appearance params change ----
  const dotsRef = useRef({ list: null, w: 0, h: 0 })
  const rebuildDots = (W, H) => {
    const s = state.current
    const rng = mulberry32(s.seed)
    const [cr, cg, cb] = hexToRgb(s.color)
    const [ar, ag, ab] = hexToRgb(s.accent)
    const cols = Math.ceil(W / s.gap)
    const rows = Math.ceil(H / s.gap)
    const aspect = W / H
    // Each dot: x, y, r, g, b, baseAlpha (brightness*vignette*density)
    const list = []
    for (let gy = 0; gy < rows; gy++) {
      for (let gx = 0; gx < cols; gx++) {
        const x = gx * s.gap + s.gap / 2
        const y = gy * s.gap + s.gap / 2
        const nx = (x / W - 0.5) * aspect
        const ny = y / H - 0.5
        const dist = Math.sqrt(nx * nx + ny * ny) / 0.62
        const vig = 1 - smoothstep(0.15, 1, dist) * s.vignette
        const base = Math.pow(rng(), 1.7)
        const baseAlpha = Math.min(1, base * s.density * vig)
        const pick = rng()
        if (baseAlpha < 0.03) continue
        let r, g, b
        if (pick < s.accentMix) { r = ar; g = ag; b = ab }
        else if (pick < s.accentMix + 0.12) {
          r = lerp(cr, 255, 0.85); g = lerp(cg, 255, 0.85); b = lerp(cb, 255, 0.85)
        } else { r = cr; g = cg; b = cb }
        list.push(x, y, r | 0, g | 0, b | 0, baseAlpha)
      }
    }
    dotsRef.current = { list: new Float32Array(list), w: W, h: H }
  }

  // ---- Single rAF loop for the lifetime of the component ----
  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let W = 0, H = 0, raf, last = 0, t0 = performance.now()
    let visible = true

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      W = Math.max(1, Math.round(rect.width))
      H = Math.max(1, Math.round(rect.height))
      canvas.width = W * dpr
      canvas.height = H * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      rebuildDots(W, H)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    let io
    if (pauseWhenOffscreen && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        ([e]) => { visible = e.isIntersecting },
        { threshold: 0 },
      )
      io.observe(canvas)
    }

    const reduce =
      respectReducedMotion &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const render = (timeSec) => {
      const s = state.current
      const env = envRef.current
      const dots = dotsRef.current.list
      // transparent background -> clear so the canvas stays see-through,
      // otherwise paint the solid fill behind the dots
      if (s.background == null || s.background === 'transparent') {
        ctx.clearRect(0, 0, W, H)
      } else {
        ctx.fillStyle = s.background
        ctx.fillRect(0, 0, W, H)
      }
      if (!env || !dots) return

      const { data, w: mw, h: mh } = env
      // original ≈ 36 px/s; reduced-motion freezes the drift
      const offset = reduce ? 0 : (timeSec * 36 * s.speed) % mw
      const shape = s.dotShape
      const circle = shape === 'circle'
      // rect uses independent w/h; square/circle fall back to dotSize
      const dw = shape === 'rect' ? (s.dotWidth ?? s.dotSize) : s.dotSize
      const dh = shape === 'rect' ? (s.dotHeight ?? s.dotSize) : s.dotSize
      const hw = dw / 2
      const hh = dh / 2

      for (let i = 0; i < dots.length; i += 6) {
        const x = dots[i], y = dots[i + 1]
        const r = dots[i + 2], g = dots[i + 3], b = dots[i + 4]
        const baseAlpha = dots[i + 5]
        // sample luminance envelope (greyscale -> red channel) with wrap
        let mx = (((x * s.maskScale + offset) % mw) + mw) % mw | 0
        let my = ((y * s.maskScale) % mh + mh) % mh | 0
        const lum = data[(my * mw + mx) * 4] / 255
        const a = baseAlpha * lum
        if (a < 0.015) continue
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`
        if (circle) { ctx.beginPath(); ctx.arc(x, y, hw, 0, 6.283); ctx.fill() }
        else ctx.fillRect(x - hw, y - hh, dw, dh)
      }
    }

    const loop = (now) => {
      raf = requestAnimationFrame(loop)
      const s = state.current
      if (s.paused || !visible) { last = now; return }
      if (s.fps > 0 && now - last < 1000 / s.fps) return
      last = now
      render((now - t0) / 1000)
    }
    raf = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      io?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseWhenOffscreen, respectReducedMotion])

  // Rebuild dots immediately when layout-affecting props change
  useEffect(() => {
    const { w, h } = dotsRef.current
    if (w && h) rebuildDots(w, h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, accent, gap, dotSize, density, vignette, accentMix, seed])

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: background == null ? 'transparent' : background,
        overflow: 'hidden',
        ...style,
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  )
}

/* ----------------------------- helpers ----------------------------- */
function mulberry32(seed) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
function hexToRgb(hex) {
  const m = hex.replace('#', '')
  const n = parseInt(m.length === 3 ? m.split('').map((c) => c + c).join('') : m, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
const lerp = (a, b, t) => a + (b - a) * t
const smoothstep = (e0, e1, x) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
  return t * t * (3 - 2 * t)
}
// Tileable smooth value-noise envelope used when no maskSrc is supplied.
function makeProceduralEnvelope(w, h) {
  const data = new Uint8ClampedArray(w * h * 4)
  const grid = 8
  const rnd = mulberry32(99)
  const g = []
  for (let i = 0; i <= grid; i++) {
    g[i] = []
    for (let j = 0; j <= grid; j++) g[i][j] = i === grid ? g[0][j === grid ? 0 : j] : (j === grid ? g[i][0] : rnd())
  }
  const fade = (t) => t * t * (3 - 2 * t)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const fx = (x / w) * grid, fy = (y / h) * grid
      const x0 = Math.floor(fx), y0 = Math.floor(fy)
      const tx = fade(fx - x0), ty = fade(fy - y0)
      const a = g[x0][y0], b = g[x0 + 1][y0], c = g[x0][y0 + 1], d = g[x0 + 1][y0 + 1]
      const v = a * (1 - tx) * (1 - ty) + b * tx * (1 - ty) + c * (1 - tx) * ty + d * tx * ty
      const l = Math.round(40 + v * 200)
      const idx = (y * w + x) * 4
      data[idx] = data[idx + 1] = data[idx + 2] = l
      data[idx + 3] = 255
    }
  }
  return { data, w, h }
}
